import React, { useState, useEffect, useContext } from "react";
import { ChevronLeft, Music2, Wifi, WifiOff } from "lucide-react";
import { auth, db } from "../firebase-config";
import { doc, setDoc, getDoc, onSnapshot, arrayUnion, updateDoc } from "firebase/firestore";
import { Message_data } from "../context/context";

export const NameThatTune = ({ onBack, clientObj, userObj }) => {
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [userGuess, setUserGuess] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [waitingForHost, setWaitingForHost] = useState(true);
  const [selectedTrack, setSelectedTrack] = useState(null);

  const { message, setMessage } = useContext(Message_data);
  const scrollTimeoutRef = React.useRef(null);
  const nameThatTuneTopRef = React.useRef(null);

  // Set up Firebase listener for the current game
  useEffect(() => {
    if (!clientObj?.clientId) {
      console.log('NameThatTune - No client ID available');
      setWaitingForHost(true);
      return;
    }

    console.log('NameThatTune - Setting up listener for client:', clientObj.clientId);
    const gameRef = doc(db, "users", clientObj.clientId, "namethattune", "current");

    const unsubscribe = onSnapshot(gameRef, (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        console.log('NameThatTune - Data received:', data);

        // Check if there's an active question
        if (data.currentQuestion) {
          setIsConnected(true);
          setWaitingForHost(false);

          // Check if user already submitted for this question
          const userId = userObj?.uid || userObj?.id;
          const userAlreadyAnswered = data.submissions?.some(
            submission => submission.userId === userId && submission.questionId === data.currentQuestion.id
          );

          if (userAlreadyAnswered) {
            // User already submitted, show their previous answer
            const userSubmission = data.submissions.find(
              submission => submission.userId === userId && submission.questionId === data.currentQuestion.id
            );
            setUserGuess(userSubmission?.userGuess || "");
            setSubmitted(true);
          } else {
            // New question, reset submission state
            setSubmitted(false);
            setUserGuess("");
          }

          // Set the current question data
          setCurrentQuestion({
            id: data.currentQuestion.id || `question-${Date.now()}`,
            previewUrl: data.currentQuestion.previewUrl || "",
            hints: data.currentQuestion.hints || [],
            correctAnswer: data.currentQuestion.correctAnswer || null
          });
        } else {
          console.log('NameThatTune - No current question set by host');
          setWaitingForHost(true);
          setIsConnected(false);
          setCurrentQuestion(null);
        }
      } else {
        console.log('NameThatTune - Document does not exist');
        setWaitingForHost(true);
        setIsConnected(false);
        setCurrentQuestion(null);
      }
    }, (error) => {
      console.error("NameThatTune - Error listening to game:", error);
      setIsConnected(false);
      setWaitingForHost(true);
    });

    return () => unsubscribe();
  }, [clientObj, userObj]);

  // Send game active state when component mounts/unmounts or game state changes
  useEffect(() => {
    if (!waitingForHost && currentQuestion) {
      // Game is active - notify Jukebox2
      console.log('NameThatTune - Sending active state: true');
      setMessage(JSON.stringify({ nameThatTuneActive: true }));
    } else {
      // Game is not active - notify Jukebox2
      console.log('NameThatTune - Sending active state: false');
      setMessage(JSON.stringify({ nameThatTuneActive: false }));
    }

    // Cleanup: send inactive when component unmounts
    return () => {
      setMessage(JSON.stringify({ nameThatTuneActive: false }));
    };
  }, [waitingForHost, currentQuestion, setMessage]);

  // Listen for track selection from JukeboxList2
  useEffect(() => {
    const handleTrackSelection = () => {
      if (message) {
        try {
          const data = JSON.parse(message);
          if (data.nameThatTuneSongSelected) {
            const track = JSON.parse(data.nameThatTuneSongSelected);
            console.log('NameThatTune - Track selected from JukeboxList2:', track);

            // Store the selected track for display
            setSelectedTrack(track);

            // Scroll back to top using the same mechanism as scroll down
            setTimeout(() => {
              const scrollMessage = {
                scrollToNameThatTune: true
              };
              setMessage(JSON.stringify(scrollMessage));
              console.log('NameThatTune - Sending scroll to top message');
            }, 100); // Small delay to ensure state updates first

            // Auto-submit if user has typed something
            if (userGuess.trim()) {
              handleSubmit(track);
            } else {
              console.log('NameThatTune - No user guess entered, skipping submission');
            }
          }
        } catch (error) {
          // Ignore parse errors
          console.error('NameThatTune - Error parsing message:', error);
        }
      }
    };
    handleTrackSelection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [message]);

  // Handle search input change - send via context to filter JukeboxList2
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setUserGuess(value);

    // Send search term and current guess via context message
    const messageData = {
      nameThatTuneSearch: value,
      nameThatTuneGuess: value,
      nameThatTuneSubmitted: submitted
    };

    console.log('NameThatTune - Sending search term:', value);
    setMessage(JSON.stringify(messageData));

    // Clear any existing scroll timeout
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    // After user types more than 3 characters and pauses for 2 seconds, scroll to JukeboxList2
    if (value.length > 3) {
      scrollTimeoutRef.current = setTimeout(() => {
        console.log('NameThatTune - Scrolling to JukeboxList2');
        // Send scroll trigger message
        const scrollMessage = {
          scrollToJukeboxList: true
        };
        setMessage(JSON.stringify(scrollMessage));
      }, 2000);
    }
  };

  const handleSubmit = async (selectedTrack = null) => {
    if (!userGuess.trim() || !currentQuestion || !clientObj?.clientId || !userObj) return;

    // Check if user already submitted for this question within 24 hours
    const userId = auth.currentUser.uid;
    try {
      const gameRef = doc(db, "users", clientObj.clientId, "namethattune", "current");
      const gameDoc = await getDoc(gameRef);

      if (gameDoc.exists()) {
        const data = gameDoc.data();
        const existingSubmission = data.submissions?.find(
          submission => submission.userId === userId && submission.questionId === currentQuestion.id
        );

        if (existingSubmission) {
          const submissionTime = new Date(existingSubmission.timestamp);
          const now = new Date();
          const timeDiff = now - submissionTime;
          const hoursDiff = timeDiff / (1000 * 60 * 60);

          // If less than 24 hours, prevent re-submission
          if (hoursDiff < 24) {
            console.log('NameThatTune - Cannot resubmit within 24 hours. Previous answer remains.');
            // Keep their previous answer visible - do nothing
            setSubmitted(true);
            return;
          }
        }
      }
    } catch (error) {
      console.error("NameThatTune - Error checking previous submission:", error);
    }

    setSubmitted(true);

    // Prepare submission data with track information if provided
    const submissionData = {
      userId: auth.currentUser.uid,
      displayName: userObj.displayName || userObj.name || userObj.email || "Anonymous",
      photoURL: userObj.photoURL || userObj.avatar || userObj.profilePicture || "",
      questionId: currentQuestion.id,
      userGuess: userGuess.trim(),
      timestamp: new Date().toISOString()
    };

    // Add selected track data if available
    if (selectedTrack) {
      submissionData.selectedTrack = {
        artist: selectedTrack.artist || "",
        track: selectedTrack.track || "",
        trackId: selectedTrack.id || selectedTrack.trackId || "",
        album: selectedTrack.album || "",
        genre: selectedTrack.genre || "",
        coverart: selectedTrack.coverart || ""
      };
      console.log('NameThatTune - Submitting with track data:', submissionData.selectedTrack);
    }

    // Save submission to Firebase for admin to judge
    try {
      const gameRef = doc(db, "users", clientObj.clientId, "namethattune", "current");

      await updateDoc(gameRef, {
        submissions: arrayUnion(submissionData)
      });

      console.log("NameThatTune - Answer submitted for admin review");

      // Notify Jukebox2 that submission is complete
      setMessage(JSON.stringify({
        nameThatTuneSubmitted: true,
        nameThatTuneGuess: userGuess.trim()
      }));
    } catch (error) {
      console.error("NameThatTune - Error submitting answer:", error);
      // If document doesn't exist, create it
      try {
        const gameRef = doc(db, "users", clientObj.clientId, "namethattune", "current");
        await setDoc(gameRef, {
          submissions: [submissionData]
        }, { merge: true });
        console.log("NameThatTune - Answer submitted (document created)");

        // Notify Jukebox2 that submission is complete
        setMessage(JSON.stringify({
          nameThatTuneSubmitted: true,
          nameThatTuneGuess: userGuess.trim()
        }));
      } catch (createError) {
        console.error("NameThatTune - Error creating submission:", createError);
      }
    }
  };

  // Show waiting screen if no question is available
  if (waitingForHost || !currentQuestion) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900">
        <div className="relative max-w-md mx-auto min-h-screen">
          <div className="absolute inset-0 bg-gradient-to-r from-cyan-400 via-purple-500 to-pink-500 opacity-20 blur-2xl"></div>

          <div className="relative bg-purple-950/90 backdrop-blur-sm min-h-screen border-x border-cyan-400/20">
            {/* Header */}
            <div className="flex items-center justify-between p-4 bg-gradient-to-r from-purple-900/50 to-indigo-900/50 backdrop-blur">
              <button
                onClick={onBack}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                aria-label="Back"
              >
                <ChevronLeft size={24} className="text-white" />
              </button>

              <h1 className="text-2xl font-bold text-white tracking-wider">
                NAME THAT TUNE
              </h1>

              <div className="flex items-center gap-2">
                <WifiOff size={20} className="text-red-400" />
              </div>
            </div>

            {/* Waiting Message */}
            <div className="flex flex-col items-center justify-center min-h-[80vh] px-6">
              <div className="text-center">
                <div className="w-32 h-32 mx-auto mb-6 bg-gradient-to-br from-purple-400 to-pink-500 rounded-full flex items-center justify-center animate-pulse">
                  <Music2 size={64} className="text-white" />
                </div>

                <h2 className="text-2xl font-bold text-white mb-4">
                  Waiting for Host
                </h2>
                <p className="text-white/70 mb-8">
                  The host will start a new question soon...
                </p>

                <button
                  onClick={onBack}
                  className="px-6 py-3 bg-red-500/20 border border-red-500/50 rounded-full text-red-400 hover:bg-red-500/30 transition-colors"
                >
                  Leave Game
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div ref={nameThatTuneTopRef} className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900">
      <div className="relative max-w-md mx-auto min-h-screen">
        {/* Neon border effect */}
        <div className="absolute inset-0 bg-gradient-to-r from-cyan-400 via-purple-500 to-pink-500 opacity-20 blur-2xl"></div>

        <div className="relative bg-purple-950/90 backdrop-blur-sm min-h-screen border-x border-cyan-400/20">
          {/* Header */}
          <div className="flex items-center justify-between p-4 bg-gradient-to-r from-purple-900/50 to-indigo-900/50 backdrop-blur">
            <button
              onClick={onBack}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              aria-label="Back"
            >
              <ChevronLeft size={24} className="text-white" />
            </button>

            <div className="text-center">
              <h1 className="text-2xl font-bold text-white tracking-wider">
                NAME THAT TUNE
              </h1>
              <div className="flex items-center justify-center gap-2 text-sm mt-1">
                {isConnected ? (
                  <>
                    <Wifi size={14} className="text-green-400" />
                    <span className="text-green-400">Connected</span>
                  </>
                ) : (
                  <>
                    <WifiOff size={14} className="text-yellow-400" />
                    <span className="text-yellow-400">Connecting...</span>
                  </>
                )}
              </div>
            </div>

            <div className="w-10" />
          </div>

          {/* Main Content */}
          <div className="flex flex-col items-center justify-center flex-1 px-4 py-8">
            {/* Audio Visualizer */}
            <div className="mb-8">
              <div className="w-48 h-48 mx-auto bg-gradient-to-br from-cyan-400/20 to-purple-500/20 rounded-full flex items-center justify-center relative">
                <div className="absolute inset-0 bg-gradient-to-r from-cyan-400 to-purple-500 rounded-full animate-pulse opacity-30"></div>
                <Music2 size={64} className="text-white relative z-10" />
              </div>
            </div>

            <h2 className="text-2xl font-bold text-white mb-4">
              Listen to the Clip
            </h2>

            {/* Audio Player - if previewUrl is provided */}
            {currentQuestion.previewUrl && (
              <div className="w-full mb-6">
                <audio
                  controls
                  src={currentQuestion.previewUrl}
                  className="w-full"
                  style={{ maxWidth: '100%' }}
                />
              </div>
            )}

            {/* Hints */}
            {currentQuestion.hints && currentQuestion.hints.length > 0 && (
              <div className="bg-purple-800/30 rounded-xl p-4 mb-6 border border-purple-600/50 w-full">
                <p className="text-yellow-400 font-medium mb-2">Hints:</p>
                {currentQuestion.hints.map((hint, index) => (
                  <p key={index} className="text-white/70 text-sm">• {hint}</p>
                ))}
              </div>
            )}

            {/* Search Input Section */}
            <div className="px-4 py-6 w-full">
              <div className="bg-purple-800/30 rounded-2xl p-6 border border-purple-600/50">
                <p className="text-white/90 text-lg leading-relaxed mb-4 text-center">
                  Type the song name to search:
                </p>

                {/* Search Input */}
                <div className="bg-purple-900/50 rounded-lg p-3 border-2 border-dashed border-cyan-400/50 mb-4">
                  {!submitted ? (
                    <input
                      type="text"
                      value={userGuess}
                      onChange={handleSearchChange}
                      placeholder="Start typing song name..."
                      className="w-full bg-black/70 text-white placeholder-white/40 focus:outline-none focus:bg-black/90 text-xl font-semibold px-2 py-1 rounded transition-colors"
                      aria-label="Song name input"
                    />
                  ) : submitted && selectedTrack ? (
                    <div className="text-white">
                      <p className="text-sm text-cyan-400 mb-3">Your answer:</p>

                      {/* Cover Art */}
                      {selectedTrack.coverart && (
                        <img
                          src={selectedTrack.coverart}
                          alt={selectedTrack.track}
                          className="w-24 h-24 rounded-lg mx-auto mb-3 shadow-lg"
                        />
                      )}

                      {/* Track Info */}
                      <p className="text-lg font-bold text-center">{selectedTrack.track}</p>
                      <p className="text-base text-white/80 text-center mb-2">{selectedTrack.artist}</p>

                      {/* User's Search */}
                      <p className="text-sm text-white/60 text-center mt-3">You searched: "{userGuess}"</p>

                      {/* Confirmation */}
                      <p className="text-sm text-green-400 mt-3 text-center">✓ Submitted for review</p>
                    </div>
                  ) : (
                    <div className="text-white">
                      <p className="text-sm text-cyan-400 mb-1">Your answer:</p>
                      <p className="text-lg font-bold">{userGuess}</p>
                      <p className="text-sm text-green-400 mt-2">✓ Submitted for review</p>
                    </div>
                  )}
                </div>

                <p className="text-white/70 text-sm text-center mb-4">
                  {submitted
                    ? 'Your answer has been submitted to the host'
                    : 'The jukebox below will filter as you type. Tap on a song to submit your answer!'
                  }
                </p>
              </div>
            </div>

            {/* Submit Button - HIDDEN - submission happens on track click */}
          </div>
        </div>
      </div>
    </div>
  );
};
